// Dates in the team time zone (Asia/Riyadh), whatever the device is set to.
export const TZ = import.meta.env.VITE_APP_TIMEZONE || 'Asia/Riyadh';

const parts = (d: Date) => {
  const f = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const p = Object.fromEntries(f.formatToParts(d).map(x => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
};
export const localDate = (d: Date = new Date()) => parts(d).date;
export const localTime = (iso: string) => parts(new Date(iso)).time;
export const localDay = (iso: string) => parts(new Date(iso)).date;

/**
 * en-GB dates with the month written as the prototype writes it: "15 Sep". Recent ICU data writes September as
 * "Sept" in en-GB, so every date with a short month goes through here.
 */
export const gbDate = (d: Date, o: Intl.DateTimeFormatOptions) => d.toLocaleDateString('en-GB', o).replace(/\bSept\b/g, 'Sep');
export const gbDateTime = (d: Date, o: Intl.DateTimeFormatOptions) => d.toLocaleString('en-GB', o).replace(/\bSept\b/g, 'Sep');

export function addDays(date: string, n: number) {
  const d = new Date(date + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** The ISO 8601 week of a day ("YYYY-MM-DD"): weeks run Monday to Sunday, week 1 is the one holding the year's first
 *  Thursday, and the week belongs to the year of its Thursday (so 29 Dec 2025 is 2026 week 1, 3 Jan 2027 is 2026
 *  week 53). */
export function isoWeek(date: string): { year: number; week: number } {
  const d = new Date(date + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + 3 - ((d.getUTCDay() + 6) % 7)); // that week's Thursday
  const year = d.getUTCFullYear();
  return { year, week: 1 + Math.floor((d.getTime() - Date.UTC(year, 0, 1, 12)) / 86_400_000 / 7) };
}
/** The ISO week a Sunday-start week strip shows, given its Sunday: the week of its Monday, which holds six of its
 *  seven days (Monday to Saturday). */
export const stripIsoWeek = (sunday: string) => isoWeek(addDays(sunday, 1));
/** "Sun 1 Nov" */
export const dayLabel = (date: string) => gbDate(new Date(date + 'T12:00:00Z'), { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
/** "1 Nov" */
export const shortDay = (date: string) => gbDate(new Date(date + 'T12:00:00Z'), { day: 'numeric', month: 'short', timeZone: 'UTC' });

/** Relative day: Today, Tomorrow, Yesterday, or "Sun 1 Nov". */
export function relDay(date: string, t = localDate()) {
  if (date === t) return 'Today';
  if (date === addDays(t, 1)) return 'Tomorrow';
  if (date === addDays(t, -1)) return 'Yesterday';
  return dayLabel(date);
}

/** "2h ago", "Yesterday", "3d ago", "12 Oct" */
/** "just now", "5m ago", "yesterday", "Mon 26 Oct": lowercase, for the middle of a sentence ("updated just now"). */
export function ago(iso: string) {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 24 && localDay(iso) === localDate()) return `${h}h ago`;
  const d = Math.floor(ms / 86400000);
  if (localDay(iso) === addDays(localDate(), -1)) return 'yesterday';
  if (d < 7) return `${Math.max(d, 1)}d ago`;
  return shortDay(localDay(iso));
}
/**
 * When something came in, for a list of things that arrived: the time today ("09:05"), "Yesterday", the weekday within a
 * week ("Tue"), else the date ("12 Oct"). today: "Today 09:05" where it stands alone.
 */
export function arrived(iso: string, opts: { today?: boolean } = {}) {
  const d = localDay(iso), t = localDate();
  if (d === t) return opts.today ? `Today ${localTime(iso)}` : localTime(iso);
  if (d === addDays(t, -1)) return 'Yesterday';
  if (d > addDays(t, -7)) return new Date(`${d}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
  return shortDay(d);
}
/** ago() where it starts a line or stands alone in a cell: "Just now", "Yesterday". */
export const agoStart = (iso: string) => { const s = ago(iso); return s[0]!.toUpperCase() + s.slice(1); };

/** Local date + time in the team zone, as an ISO string with offset (what the API expects). */
export function toIsoAt(date: string, time: string) {
  const guess = new Date(`${date}T${time}:00Z`);
  const shown = parts(guess);
  const diff = new Date(`${shown.date}T${shown.time}:00Z`).getTime() - guess.getTime();
  return new Date(guess.getTime() - diff).toISOString();
}

export const bytes = (n: number) => n < 1024 ? `${n} B` : n < 1048576 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`;
export const ext = (name: string) => (name.split('.').pop() ?? '').slice(0, 4);
